import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { AlertTriangle, Merge, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { restaurantsAPI } from '../lib/api';

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const RecordCard = ({ r, selected, onSelect }) => (
  <button
    type="button"
    data-testid={`merge-keep-${r.id}`}
    onClick={() => onSelect(r.id)}
    className={`text-left rounded-md border p-3 transition-colors ${selected ? 'border-ica-navy bg-ica-navy/5 ring-1 ring-ica-navy' : 'border-gray-200 hover:border-gray-300'}`}
  >
    <div className="flex items-center justify-between gap-2">
      <p className="font-semibold text-sm text-ica-navy truncate">{r.name}</p>
      {selected && <span className="text-[10px] font-semibold uppercase text-ica-navy">Keep</span>}
    </div>
    <p className="text-[11px] text-gray-500">Created {fmt(r.created_at)}{r.contact_person ? ` · ${r.contact_person}` : ''}{r.email ? ` · ${r.email}` : ''}</p>
    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-gray-600">
      <span>{r.placements} placement{r.placements === 1 ? '' : 's'}</span>
      <span>{r.cycles} cycle{r.cycles === 1 ? '' : 's'}</span>
      <span>{r.contracts} contract{r.contracts === 1 ? '' : 's'} ({r.signed_contracts} signed)</span>
      <span>{r.feedback_forms} feedback form{r.feedback_forms === 1 ? '' : 's'}</span>
    </div>
    {r.signed_eta && (
      <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700" data-testid={`merge-has-eta-${r.id}`}>
        <CheckCircle2 className="w-3 h-3" /> Signed ETA on this record
      </p>
    )}
  </button>
);

const MergeDialog = ({ group, onClose, onMerged }) => {
  const withEta = group.restaurants.find((r) => r.signed_eta);
  const [keepId, setKeepId] = useState((withEta || group.restaurants[0]).id);
  const [busy, setBusy] = useState(false);
  const others = group.restaurants.filter((r) => r.id !== keepId);

  const merge = async () => {
    setBusy(true);
    try {
      for (const o of others) await restaurantsAPI.merge(keepId, o.id);
      toast.success(`Merged ${others.length} duplicate record${others.length === 1 ? '' : 's'} into "${group.name}"`);
      onMerged();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Merge failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle>Merge duplicates — {group.name}</DialogTitle>
          <DialogDescription>
            Choose the record to keep. Placements, contracts (incl. the signed ETA), feedback forms and cycle memberships from the other record{others.length === 1 ? '' : 's'} move onto it; the duplicate is then removed. Blank contact details on the kept record are filled from the duplicate.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {group.restaurants.map((r) => <RecordCard key={r.id} r={r} selected={r.id === keepId} onSelect={setKeepId} />)}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="merge-confirm-btn" onClick={merge} disabled={busy || others.length === 0}>
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Merge className="w-4 h-4 mr-2" />}
            Merge into kept record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DuplicateRestaurantsBanner = ({ canWrite, onMerged }) => {
  const [groups, setGroups] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => restaurantsAPI.duplicates().then((r) => setGroups(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  if (groups.length === 0) return null;
  return (
    <div data-testid="duplicate-restaurants-banner" className="rounded-md border border-amber-300 bg-amber-50 p-3 flex flex-col sm:flex-row sm:items-center gap-3">
      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
      <div className="flex-1 text-sm text-amber-900">
        <strong>{groups.length} restaurant name{groups.length === 1 ? ' has' : 's have'} duplicate records.</strong>{' '}
        Documents and contracts get split across them — merge each pair so signed ETAs show on every placement.
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {groups.map((g) => (
            <Button
              key={g.name}
              size="sm"
              variant="outline"
              data-testid={`merge-open-${g.restaurants[0].id}`}
              className="h-7 text-xs bg-white border-amber-300 text-amber-900 hover:bg-amber-100"
              disabled={!canWrite}
              onClick={() => setActive(g)}
            >
              <Merge className="w-3 h-3 mr-1" /> {g.name} <span className="ml-1 text-amber-600">×{g.restaurants.length}</span>
            </Button>
          ))}
        </div>
      </div>
      {active && (
        <MergeDialog group={active} onClose={() => setActive(null)} onMerged={() => { setActive(null); load(); onMerged?.(); }} />
      )}
    </div>
  );
};

export default DuplicateRestaurantsBanner;
